import { useTrans } from "@/translations";

import { useCallback, useContext, useMemo } from "react";

import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { RecyclingRateTrendContext } from "../context";
import { ChartType, RecyclingRatePoint } from "../types";
import { RecyclingRateTooltip } from "./tooltip";

type Props = {
  data: RecyclingRatePoint[];
};

export const RecyclingRateTrendGraph: React.FC<Props> = ({ data }) => {
  const { t } = useTrans();
  const {
    target,
    chartType,
    showCompareYear,
    showTarget,
    showPortfolioAverage,
  } = useContext(RecyclingRateTrendContext);
  const isBarChart = useMemo(() => chartType === ChartType.BAR, [chartType]);

  const lblStep = useMemo(
    () => Math.max(1, Math.round(data.length / 12)),
    [data],
  );

  const renderRateLabel = useCallback(
    (props: any, isBar: boolean, fillColor: string) => {
      const { x, y, width, value, index } = props;
      if (index % lblStep !== 0 && index !== data.length - 1) {
        return null;
      }

      const cx = isBar ? x + width / 2 : x;
      const cy = isBar ? y - 5.5 : y - 10;

      return (
        <text
          key={`label-${index}`}
          x={cx}
          y={cy}
          textAnchor="middle"
          fontSize={10}
          fontWeight={isBar ? 700 : 600}
          fill={fillColor}
        >
          {value.toFixed(1)}%
        </text>
      );
    },
    [data.length, lblStep],
  );

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={data}
        margin={{ top: 32.5, right: 20, left: 0, bottom: 0 }}
      >
        <defs>
          <linearGradient id="rateGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5B8C3E" stopOpacity={0.22} />
            <stop offset="62%" stopColor="#5B8C3E" stopOpacity={0.06} />
            <stop offset="100%" stopColor="#5B8C3E" stopOpacity={0} />
          </linearGradient>
        </defs>

        <CartesianGrid stroke="#EEE7E7" vertical={false} />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          interval={lblStep - 1}
          tick={{ fontSize: 10, fill: "#6F6C6C" }}
        />
        <YAxis
          domain={[0, 100]}
          ticks={[0, 25, 50, 75, 100]}
          tickFormatter={(v) => `${v}%`}
          tickLine={false}
          axisLine={false}
          width={47}
          tick={{ fontSize: 10.5, fill: "#B2ABAB" }}
        />
        <Tooltip
          content={<RecyclingRateTooltip />}
          cursor={{ fill: "rgba(0,0,0,0.04)" }}
        />
        {isBarChart ? (
          <>
            {showCompareYear && (
              <Bar
                dataKey="prevYearRate"
                name="Year before"
                fill="#C5BEBE"
                radius={[1.65, 1.65, 0, 0]}
                label={(props: any) => renderRateLabel(props, true, "#C5BEBE")}
              />
            )}
            <Bar
              dataKey="rate"
              name="This year"
              fill="#5B8C3E"
              radius={[2.475, 2.475, 0, 0]}
              label={(props: any) => renderRateLabel(props, true, "#5B8C3E")}
            />
          </>
        ) : (
          <>
            <Area
              type="monotone"
              dataKey="rate"
              stroke="none"
              fill="url(#rateGradient)"
              isAnimationActive={false}
            />
            {showCompareYear && (
              <Line
                type="monotone"
                dataKey="prevYearRate"
                name="Year before"
                stroke="#C5BEBE"
                strokeWidth={1.2375}
                strokeDasharray="4.95 3.3"
                dot={false}
                activeDot={false}
              />
            )}
            <Line
              type="monotone"
              dataKey="rate"
              name="This year"
              stroke="#5B8C3E"
              strokeWidth={2.0625}
              dot={(props: any) => {
                const { cx, cy, index } = props;
                const isLast = index === data.length - 1;
                return (
                  <circle
                    key={`dot-${index}`}
                    cx={cx}
                    cy={cy}
                    r={isLast ? 3.465 : 2.145}
                    fill={isLast ? "#5B8C3E" : "#fff"}
                    stroke="#5B8C3E"
                    strokeWidth={isLast ? 0 : 1.32}
                  />
                );
              }}
              activeDot={{ r: 4.125 }}
              label={(props: any) => renderRateLabel(props, false, "#5B8C3E")}
            />
          </>
        )}
        {showPortfolioAverage && (
          <Line
            type="monotone"
            dataKey="portfolioAverage"
            name="Portfolio average"
            stroke="#B73A23"
            strokeWidth={1.2375}
            strokeDasharray="4.95 3.3"
            dot={false}
            activeDot={false}
          />
        )}
        {showTarget && (
          <ReferenceLine
            y={target}
            stroke="#467432"
            strokeWidth={1.2375}
            strokeDasharray="4.95 3.3"
            label={(props: any) => {
              const { viewBox } = props;
              const text = t("homePage:recyclingRateTarget", {
                targetValue: target,
              });
              const width = text.length * 7 + 16;
              const x = viewBox.x + viewBox.width - width;
              const y = viewBox.y - 18;
              return (
                <g>
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={16}
                    rx={2.475}
                    fill="#2F5A22"
                  />
                  <text
                    x={x + width / 2}
                    y={y + 11.5}
                    textAnchor="middle"
                    fontSize={9.5}
                    fontWeight={700}
                    fill="#fff"
                  >
                    {text}
                  </text>
                </g>
              );
            }}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
